import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import AvatarEditor, { type AvatarEditorRef } from 'react-avatar-editor';

import type { IconType } from '../../../icons/constants';
import Icon, { ICONS } from '../../../icons/icon';
import Button from '../../../ui/button/button';
import FileButton from '../../../ui/forms/fields/file_button/file_button';
import Slider from '../../../ui/slider/slider';

import styles from './avatar.module.css';

type AvatarProps = {
  onChange: (blob: Blob | null) => void;
  onRemove: () => void;
  photo?: string;
  icon?: IconType;
  edit?: boolean;
  readOnly?: boolean;
  width?: number;
  height?: number;
  borderRadius?: number;
  min?: number;
  scaleEnabled?: boolean;
};

export function Avatar({
  onChange,
  onRemove,
  photo,
  icon = ICONS.User,
  edit = false,
  readOnly,
  width = 200,
  height = 200,
  borderRadius = 200,
  min = 0.1,
  scaleEnabled = true,
}: AvatarProps) {
  const [file, setFile] = useState<File | string | null>(null);
  const [isEditing, setIsEditing] = useState(edit);
  const [scale, setScale] = useState(1);
  const [isUploading, setIsUploading] = useState(false);
  const avatarRef = useRef<AvatarEditorRef>(null);

  const fileRef = useRef(file);
  fileRef.current = file;

  const replaceFile = (nextFile: File | string | null): void => {
    const currentFile = fileRef.current;
    if (typeof currentFile === 'string' && currentFile !== nextFile) {
      URL.revokeObjectURL(currentFile);
    }
    setFile(nextFile);
  };

  useEffect(() => {
    return () => {
      if (typeof fileRef.current === 'string') {
        URL.revokeObjectURL(fileRef.current);
      }
    };
  }, []);

  const [previousPhoto, setPreviousPhoto] = useState(photo);
  if (previousPhoto !== photo) {
    setPreviousPhoto(photo);
    if (isUploading) {
      setIsUploading(false);
    }
  }

  const handleScale = (value: number) => {
    setScale(Math.round(value * 10) / 10);
  };

  const handleCancel = () => {
    setIsEditing(false);
    replaceFile(null);
    setScale(1);
  };

  const handleSave = () => {
    const current = avatarRef.current;
    if (!current) {
      return;
    }
    setIsUploading(true);
    current.getImageScaledToCanvas().toBlob((blob) => {
      if (!blob) {
        return;
      }
      replaceFile(URL.createObjectURL(blob));
      setIsEditing(false);
      setScale(1);
      onChange(blob);
    });
  };

  const handleUploadFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) {
      return;
    }
    setIsEditing(true);
    setScale(1.2);
    Array.from(event.target.files).forEach((uploadedFile) => {
      replaceFile(uploadedFile);
    });
  };

  if (isEditing) {
    const editedPhoto = file ?? photo;

    return (
      <div>
        <div className={styles.edit_wrap}>
          <AvatarEditor
            ref={avatarRef}
            image={editedPhoto as string}
            width={width}
            height={height}
            border={20}
            borderRadius={borderRadius}
            color={[5, 6, 7, 0.75]}
            crossOrigin="anonymous"
            scale={scaleEnabled ? scale : 1}
          />
        </div>
        <FileButton
          className={styles.edit_input}
          multiple={false}
          name="picture"
          accept="image/*"
          onChange={handleUploadFile}
          message="Choose a Picture"
        />
        <div>
          {scaleEnabled && editedPhoto ? (
            <div className={styles.slider_wrapper}>
              <Icon icon={ICONS.Expand} className={styles.icon} />
              <div className={styles.slider}>
                <Slider
                  value={scale}
                  step={0.01}
                  min={min}
                  max={2}
                  onChange={handleScale}
                />
              </div>
            </div>
          ) : null}
        </div>
        <div className={styles.controls}>
          <Button variant="info" noBorder onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="success" noBorder onClick={handleSave}>
            Save
          </Button>
        </div>
      </div>
    );
  }

  const shownPhoto = file || photo;

  const avatarStyle = { width, height, borderRadius };
  const imageStyle = {
    backgroundImage: shownPhoto ? `url(${shownPhoto})` : '',
    backgroundSize: width === height ? `${width}px` : 'unset',
  };
  const iconSize = Math.min(width, height) * 0.45;

  return (
    <div className={styles.wrapper}>
      <div
        className={isUploading ? styles.bubble_loading : styles.bubble}
        style={avatarStyle}
      >
        {shownPhoto ? (
          <div className={styles.image} style={imageStyle} />
        ) : (
          <Icon icon={icon} size={iconSize} />
        )}
      </div>
      {!readOnly && !isUploading ? (
        <div className={styles.actions}>
          <FileButton
            multiple={false}
            buttonProps={{ className: styles.file_button }}
            name="picture"
            accept="image/*"
            onChange={handleUploadFile}
            message={shownPhoto ? 'Change photo' : 'Upload Photo'}
          />
          {shownPhoto ? (
            <Button
              className={styles.remove_button}
              disabled={isUploading}
              variant="danger"
              onClick={() => {
                onRemove();
                if (file) {
                  replaceFile(null);
                }
              }}
              type="button"
            >
              Remove Photo
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
